/**
 * Equity Curve Fan Mock
 *
 * Shows the Monte Carlo fan chart with P5/P25/P50/P75/P95
 * percentile bands spreading out over trade count,
 * illustrating the range of possible equity paths.
 */

import {
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';

// Percentile bands (cumulative return %)
const bands = {
  p5: [0, -1.2, -2.8, -3.5, -4.0, -3.2, -2.5, -1.8, -0.5, 0.8, 2.0, 3.5, 5.0, 6.5, 7.5, 8.0],
  p25: [0, 0.5, 1.0, 1.8, 3.0, 4.5, 5.8, 7.2, 8.5, 10.0, 11.5, 13.0, 14.5, 16.0, 17.5, 19.0],
  p50: [0, 1.5, 3.0, 4.8, 6.5, 8.5, 10.2, 12.0, 13.8, 15.5, 17.2, 19.0, 20.8, 22.5, 24.0, 25.5],
  p75: [0, 2.5, 5.0, 7.8, 10.0, 12.5, 14.8, 17.0, 19.2, 21.5, 23.5, 25.5, 27.5, 29.5, 31.0, 32.5],
  p95: [0, 4.0, 8.0, 12.0, 15.5, 19.0, 22.0, 25.0, 27.5, 30.0, 32.5, 35.0, 37.0, 39.0, 41.0, 43.0],
};

// Stacked area data: base (p5) + band deltas
const data = bands.p5.map((_, i) => ({
  trade: i,
  base: bands.p5[i],
  bandLow: bands.p25[i] - bands.p5[i],
  bandMid: bands.p75[i] - bands.p25[i],
  bandHigh: bands.p95[i] - bands.p75[i],
  p50: bands.p50[i],
}));

const tipStyle = {
  background: 'rgba(17,24,39,0.95)',
  border: '1px solid rgba(75,85,99,0.5)',
  borderRadius: 6,
  fontSize: 11,
  fontFamily: 'ui-monospace, monospace',
};

function CustomTooltip({ active, label }: { active?: boolean; label?: number }) {
  if (!active || label == null || label < 0 || label >= bands.p50.length) return null;
  const i = label;
  return (
    <div style={{ ...tipStyle, padding: '0.375rem 0.5rem' }}>
      <div style={{ color: '#94a3b8', marginBottom: 2 }}>Trade {i}</div>
      <div style={{ color: '#3b82f6' }}>Median: {bands.p50[i].toFixed(1)}%</div>
      <div style={{ color: '#60a5fa', fontSize: 10 }}>P25-P75: {bands.p25[i].toFixed(1)}% to {bands.p75[i].toFixed(1)}%</div>
      <div style={{ color: '#64748b', fontSize: 10 }}>P5-P95: {bands.p5[i].toFixed(1)}% to {bands.p95[i].toFixed(1)}%</div>
    </div>
  );
}

export default function EquityCurveFanMock() {
  return (
    <div className="platform-mock">
      <div className="platform-mock-header">
        <span className="dot" /> Equity Curve Fan &mdash; Confidence Bands
      </div>
      <div className="platform-mock-body" style={{ padding: '0.75rem' }}>
        <ResponsiveContainer width="100%" height={160}>
          <ComposedChart data={data} margin={{ top: 8, right: 8, bottom: 16, left: 4 }}>
            <CartesianGrid stroke="#334155" strokeWidth={0.5} vertical={false} />
            <XAxis
              dataKey="trade"
              tick={{ fill: '#64748b', fontSize: 9 }}
              axisLine={false}
              tickLine={false}
              label={{ value: 'Trade Number', position: 'insideBottom', offset: -8, fill: '#64748b', fontSize: 10 }}
            />
            <YAxis
              tick={{ fill: '#64748b', fontSize: 9 }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(v: number) => `${v}%`}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ stroke: 'rgba(255,255,255,0.1)' }} />
            <ReferenceLine y={0} stroke="#475569" strokeWidth={0.8} strokeDasharray="3 2" />
            <Area dataKey="base" stackId="fan" fill="transparent" stroke="none" isAnimationActive={false} />
            <Area dataKey="bandLow" stackId="fan" fill="#3b82f6" fillOpacity={0.06} stroke="none" isAnimationActive={false} />
            <Area dataKey="bandMid" stackId="fan" fill="#3b82f6" fillOpacity={0.12} stroke="none" isAnimationActive={false} />
            <Area dataKey="bandHigh" stackId="fan" fill="#3b82f6" fillOpacity={0.06} stroke="none" isAnimationActive={false} />
            <Line dataKey="p50" stroke="#3b82f6" strokeWidth={2} dot={false} isAnimationActive={false} />
          </ComposedChart>
        </ResponsiveContainer>

        <div style={{ display: 'flex', justifyContent: 'center', gap: '0.75rem', marginTop: '0.25rem' }}>
          {[
            { label: 'P5 / P95', color: '#64748b', dash: true },
            { label: 'P25 / P75', color: '#60a5fa', dash: false },
            { label: 'P50 (Median)', color: '#3b82f6', dash: false },
          ].map((item) => (
            <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
              <div style={{
                width: 10, height: item.dash ? 1 : 2,
                backgroundColor: item.color,
                borderRadius: 1,
                borderTop: item.dash ? `1px dashed ${item.color}` : 'none',
              }} />
              <span style={{ fontSize: '0.5625rem', color: '#94a3b8' }}>{item.label}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="platform-mock-caption">
        The fan widens with more trades — wider bands mean higher outcome uncertainty.
      </div>
    </div>
  );
}
